import {
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { APP_ROUTES } from '../../../shared/constants/routes';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

const EXCLUDED_URLS = ['/Auth/Login', '/Auth/RefreshToken', '/Auth/Register'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (isExcludedUrl(req.url)) {
        return next(req);
    }

    if (authService.isRefreshTokenExpired()) {
        return handleExpiredSession(authService, router);
    }

    if (authService.shouldRefreshToken()) {
        return refreshAndRetry(req, next, authService, router);
    }

    return executeRequest(req, next, authService, router);
};

function isExcludedUrl(url: string): boolean {
    return EXCLUDED_URLS.some((excludedUrl) => url.includes(excludedUrl));
}

function handleExpiredSession(
    authService: AuthService,
    router: Router,
): Observable<never> {
    authService.logout();
    router.navigate([APP_ROUTES.login]);
    return throwError(() => new Error('Session expired'));
}

/**
 * Refresh the token, then replay the request with the fresh credential.
 *
 * The refresh itself is delegated to AuthService.refreshToken(), which is the
 * single source of truth for coordination (AB#8996): concurrent requests all
 * subscribe to the same in-flight refresh rather than each keeping their own
 * flag. Because that shared stream both emits on success AND errors on
 * failure, a failed refresh no longer leaves parked requests hanging — the
 * error propagates here so every waiting request fails deterministically and
 * the user is redirected to login (AB#9024).
 */
function refreshAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    router: Router,
): Observable<HttpEvent<unknown>> {
    return authService.refreshToken().pipe(
        switchMap(() => next(addAuthHeader(req, authService.getToken()))),
        catchError((error) => {
            // AuthService.refreshToken() has already logged out on failure;
            // force the redirect and surface the error to the caller.
            router.navigate([APP_ROUTES.login]);
            return throwError(() => error);
        }),
    );
}

function executeRequest(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    router: Router,
): Observable<HttpEvent<unknown>> {
    return next(addAuthHeader(req, authService.getToken())).pipe(
        catchError((error) => {
            if (error.status === 401) {
                if (authService.isRefreshTokenExpired()) {
                    return handleExpiredSession(authService, router);
                }

                // A 401 triggers a single coordinated refresh; the retried
                // request below is issued with raw next() so it cannot loop
                // back into another refresh.
                return refreshAndRetry(req, next, authService, router);
            }

            return throwError(() => error);
        }),
    );
}

function addAuthHeader(
    req: HttpRequest<unknown>,
    token: string | null,
): HttpRequest<unknown> {
    if (!token) return req;

    return req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
    });
}
